import { createClient } from '@supabase/supabase-js';
import { saveToLocalStorage, getFromLocalStorage } from './localDataService';

// Use environment variables with fallbacks (though they should always be set)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Check if we should use mock data (when Supabase credentials are missing)
const USE_MOCK_DATA = !supabaseUrl || !supabaseAnonKey;

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
  global: {
    headers: {
      'X-Client-Info': 'onsen-life-web-app',
    },
  },
});

// Create a mock Supabase client for local development without Supabase
if (USE_MOCK_DATA) {
  console.warn('Using mock Supabase client - real Supabase URL and Anon Key are missing');
  
  // Override Supabase methods with mock implementations
  Object.assign(supabase, {
    from: (tableName: string) => {
      const createQueryBuilder = (initialData?: any[], filters?: any[]) => {
        const storedData = getFromLocalStorage(tableName) || {};
        let data = initialData || storedData[tableName] || [];
        let appliedFilters = filters || [];

        const queryBuilder = {
          select: (query: string = '*', options: any = {}) => {
            // Handle grouped select queries
            if (query.includes('count') && appliedFilters.some((f: any) => f.type === 'group')) {
              const groupColumn = appliedFilters.find((f: any) => f.type === 'group')?.column;
              if (groupColumn) {
                // Group by the specified column and count
                const grouped = data.reduce((acc: any, item: any) => {
                  const key = item[groupColumn];
                  if (key) {
                    acc[key] = (acc[key] || 0) + 1;
                  }
                  return acc;
                }, {});

                // Convert to array format expected by the component
                const result = Object.entries(grouped).map(([key, count]) => ({
                  [groupColumn]: key,
                  count: count
                }));

                return Promise.resolve({ data: result, error: null });
              }
            }

            return queryBuilder;
          },
          eq: (column: string, value: any) => {
            const filtered = data.filter((item: any) => item[column] === value);
            return createQueryBuilder(filtered, appliedFilters);
          },
          not: (column: string, operator: string, value: any) => {
            let filtered = data;
            if (operator === 'is' && value === null) {
              filtered = data.filter((item: any) => item[column] != null);
            } else {
              filtered = data.filter((item: any) => item[column] !== value);
            }
            return createQueryBuilder(filtered, appliedFilters);
          },
          group: (column: string) => {
            const newFilters = [...appliedFilters, { type: 'group', column }];
            return createQueryBuilder(data, newFilters);
          },
          gte: (column: string, value: any) => {
            const filtered = data.filter((item: any) => {
              const itemValue = parseFloat(item[column]);
              return !isNaN(itemValue) && itemValue >= value;
            });
            return createQueryBuilder(filtered, appliedFilters);
          },
          lte: (column: string, value: any) => {
            const filtered = data.filter((item: any) => {
              const itemValue = parseFloat(item[column]);
              return !isNaN(itemValue) && itemValue <= value;
            });
            return createQueryBuilder(filtered, appliedFilters);
          },
          maybeSingle: async () => {
            const item = data[0] || null;
            return { data: item, error: null };
          },
          single: async () => {
            const item = data[0] || null;
            return { data: item, error: null };
          },
          limit: (limit: number) => {
            const limited = data.slice(0, limit);
            return createQueryBuilder(limited, appliedFilters);
          },
          order: (orderColumn: string, { ascending }: { ascending: boolean } = { ascending: true }) => {
            const sorted = [...data].sort((a, b) => {
              if (ascending) {
                return a[orderColumn] > b[orderColumn] ? 1 : -1;
              } else {
                return a[orderColumn] < b[orderColumn] ? 1 : -1;
              }
            });
            return createQueryBuilder(sorted, appliedFilters);
          },
          range: (start: number, end: number) => {
            const ranged = data.slice(start, end + 1);
            return Promise.resolve({ data: ranged, error: null });
          },
          then: (resolve: Function) => {
            // Make the query builder thenable for async operations
            return resolve({ data, error: null });
          }
        };

        // Add async execution method
        Object.defineProperty(queryBuilder, 'then', {
          value: (resolve: Function) => {
            return Promise.resolve({ data, error: null }).then(resolve);
          },
          writable: false,
          enumerable: false
        });

        return queryBuilder;
      };

      return {
        ...createQueryBuilder(),
        insert: (data: any) => {
          const storedData = getFromLocalStorage(tableName) || {};
          const items = storedData[tableName] || [];
          
          // Add generated IDs and timestamps if not present
          const newItem = {
            id: data.id || `mock-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            created_at: data.created_at || new Date().toISOString(),
            ...data
          };
          
          items.push(newItem);
          storedData[tableName] = items;
          saveToLocalStorage(tableName, storedData);
          
          return Promise.resolve({ data: newItem, error: null });
        },
        update: (data: any) => {
          return {
            eq: (column: string, value: any) => {
              const storedData = getFromLocalStorage(tableName) || {};
              const items = storedData[tableName] || [];
              
              const index = items.findIndex((i: any) => i[column] === value);
              if (index !== -1) {
                items[index] = { ...items[index], ...data, updated_at: new Date().toISOString() };
                storedData[tableName] = items;
                saveToLocalStorage(tableName, storedData);
                return Promise.resolve({ data: items[index], error: null });
              }
              
              return Promise.resolve({ data: null, error: null });
            }
          };
        },
        delete: () => {
          return {
            eq: (column: string, value: any) => {
              const storedData = getFromLocalStorage(tableName) || {};
              const items = storedData[tableName] || [];
              
              const newItems = items.filter((i: any) => i[column] !== value);
              storedData[tableName] = newItems;
              saveToLocalStorage(tableName, storedData);
              
              return Promise.resolve({ data: null, error: null });
            }
          };
        },
        upsert: (data: any) => {
          const storedData = getFromLocalStorage(tableName) || {};
          const items = storedData[tableName] || [];
          
          // Check if item exists
          const index = items.findIndex((i: any) => i.id === data.id);
          
          if (index !== -1) {
            // Update existing item
            items[index] = { ...items[index], ...data, updated_at: new Date().toISOString() };
          } else {
            // Insert new item
            items.push({
              id: data.id || `mock-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              created_at: data.created_at || new Date().toISOString(),
              ...data
            });
          }
          
          storedData[tableName] = items;
          saveToLocalStorage(tableName, storedData);
          
          return Promise.resolve({ data, error: null });
        }
      };
    },
    storage: {
      from: (bucketName: string) => {
        return {
          upload: (path: string, file: File, options: any = {}) => {
            // Mock successful upload
            return Promise.resolve({ data: { path }, error: null });
          },
          getPublicUrl: (path: string) => {
            // Return mock URL
            return { data: { publicUrl: `https://example.com/${bucketName}/${path}` } };
          },
          remove: (paths: string[]) => {
            // Mock successful removal
            return Promise.resolve({ data: { paths }, error: null });
          }
        };
      }
    },
    auth: {
      getSession: async () => {
        const mockSession = getFromLocalStorage('session');
        return { data: { session: mockSession }, error: null };
      },
      getUser: async () => {
        const mockUser = getFromLocalStorage('user');
        return { data: { user: mockUser }, error: null };
      },
      signInWithPassword: async ({ email, password }: { email: string, password: string }) => {
        // Simple mock authentication
        if (email && password) {
          const mockUser = { id: 'mock-user-id', email };
          const mockSession = { user: mockUser, access_token: 'mock-token' };
          
          saveToLocalStorage('user', mockUser);
          saveToLocalStorage('session', mockSession);
          
          return { data: { user: mockUser, session: mockSession }, error: null };
        }
        return { data: { user: null, session: null }, error: { message: 'Invalid credentials' } };
      },
      signUp: async ({ email, password }: { email: string, password: string }) => {
        if (email && password) {
          const mockUser = { id: 'mock-user-id', email };
          saveToLocalStorage('pendingUser', mockUser);
          return { data: { user: null, session: null }, error: null };
        }
        return { data: { user: null, session: null }, error: { message: 'Invalid email or password' } };
      },
      signInWithOAuth: async ({ provider }: { provider: string }) => {
        // Mock successful OAuth redirect
        const mockUser = { id: 'mock-oauth-user-id', email: 'oauth@example.com' };
        const mockSession = { user: mockUser, access_token: 'mock-oauth-token' };
        
        saveToLocalStorage('user', mockUser);
        saveToLocalStorage('session', mockSession);
        
        return { data: { provider, url: '#' }, error: null };
      },
      signOut: async () => {
        // Clear the session and user
        localStorage.removeItem('onsenlife_user');
        localStorage.removeItem('onsenlife_session');
        return { error: null };
      },
      onAuthStateChange: (callback: Function) => {
        // No real subscription needed for mock
        return { data: { subscription: { unsubscribe: () => {} } } };
      }
    }
  });
}